import { buildApiUrl } from "@/lib/api/config"
import { apiPaths } from "@/lib/api/backendUrls"

export type ProductAnalyticsEventName =
  | "page_viewed"
  | "call_to_action_clicked"
  | "form_started"
  | "form_completed"
  | "search_performed"
  | "registration_completed"
  | "deposit_submitted"
  | "certificate_downloaded"
  | "project_created"
  | "project_submitted"
  | "project_evaluation_submitted"
  | "project_team_invitation_sent"
  | "event_registration_completed"
  | "event_check_in_completed"
  | "profile_pdf_downloaded"
  | "project_export_downloaded"
  | "event_export_downloaded"

export type ProductAnalyticsArea =
  | "auth"
  | "profile"
  | "events"
  | "deposits"
  | "projects"
  | "notifications"
  | "other"

export type ProductAnalyticsAuthState = "anonymous" | "authenticated"

export type ProductAnalyticsEvent = Readonly<{
  eventName: ProductAnalyticsEventName
  area: ProductAnalyticsArea
  authState: ProductAnalyticsAuthState
}>

type AnalyticsTransport = (events: readonly ProductAnalyticsEvent[]) => Promise<boolean>

type QueueOptions = {
  enabled: boolean
  transport: AnalyticsTransport
  batchSize?: number
  maxQueueSize?: number
  flushDelayMs?: number
  maxRetries?: number
  retryBaseDelayMs?: number
}

const sleep = (delayMs: number) => new Promise<void>((resolve) => setTimeout(resolve, delayMs))

export class ProductAnalyticsQueue {
  private readonly enabled: boolean
  private readonly transport: AnalyticsTransport
  private readonly batchSize: number
  private readonly maxQueueSize: number
  private readonly flushDelayMs: number
  private readonly maxRetries: number
  private readonly retryBaseDelayMs: number
  private queue: ProductAnalyticsEvent[] = []
  private timer: ReturnType<typeof setTimeout> | null = null
  private flushing = false

  constructor(options: QueueOptions) {
    this.enabled = options.enabled
    this.transport = options.transport
    this.batchSize = options.batchSize ?? 20
    this.maxQueueSize = options.maxQueueSize ?? 100
    this.flushDelayMs = options.flushDelayMs ?? 1_000
    this.maxRetries = options.maxRetries ?? 2
    this.retryBaseDelayMs = options.retryBaseDelayMs ?? 500
  }

  enqueue(event: ProductAnalyticsEvent): void {
    if (!this.enabled) return
    this.queue.push(event)
    if (this.queue.length > this.maxQueueSize) {
      this.queue.splice(0, this.queue.length - this.maxQueueSize)
    }
    if (this.queue.length >= this.batchSize) {
      void this.flush()
      return
    }
    this.scheduleFlush()
  }

  async flush(): Promise<void> {
    if (!this.enabled || this.flushing || this.queue.length === 0) return
    this.clearTimer()
    this.flushing = true
    const batch = this.queue.splice(0, this.batchSize)
    try {
      for (let attempt = 0; attempt <= this.maxRetries; attempt += 1) {
        try {
          if (await this.transport(batch)) return
        } catch {
          // Analytics is best-effort and must never surface errors to product flows.
        }
        if (attempt < this.maxRetries) {
          await sleep(this.retryBaseDelayMs * 2 ** attempt)
        }
      }
    } finally {
      this.flushing = false
      if (this.queue.length > 0) this.scheduleFlush()
    }
  }

  private scheduleFlush(): void {
    if (this.timer !== null || this.flushing) return
    this.timer = setTimeout(() => {
      this.timer = null
      void this.flush()
    }, this.flushDelayMs)
  }

  private clearTimer(): void {
    if (this.timer === null) return
    clearTimeout(this.timer)
    this.timer = null
  }
}

const analyticsEnabled = () => {
  const configured = import.meta.env.VITE_PRODUCT_ANALYTICS_ENABLED?.trim().toLowerCase()
  if (configured === "0" || configured === "false" || configured === "off") return false
  return typeof navigator === "undefined" || navigator.doNotTrack !== "1"
}

const eventValues: Record<ProductAnalyticsEventName, number> = {
  page_viewed: 1,
  call_to_action_clicked: 2,
  form_started: 3,
  form_completed: 4,
  search_performed: 5,
  registration_completed: 6,
  deposit_submitted: 7,
  certificate_downloaded: 8,
  project_created: 9,
  project_submitted: 10,
  project_evaluation_submitted: 11,
  project_team_invitation_sent: 12,
  event_registration_completed: 13,
  event_check_in_completed: 14,
  profile_pdf_downloaded: 15,
  project_export_downloaded: 16,
  event_export_downloaded: 17,
}

const areaValues: Record<ProductAnalyticsArea, number> = {
  auth: 1,
  profile: 2,
  projects: 3,
  events: 4,
  deposits: 5,
  notifications: 6,
  other: 7,
}

const authStateValues: Record<ProductAnalyticsAuthState, number> = {
  anonymous: 2,
  authenticated: 3,
}

export const toProductAnalyticsBatch = (events: readonly ProductAnalyticsEvent[]) => ({
  events: events.map((event) => ({
    event: eventValues[event.eventName],
    area: areaValues[event.area],
    authState: authStateValues[event.authState],
  })),
})

const sendBatch: AnalyticsTransport = async (events) => {
  const response = await fetch(buildApiUrl(apiPaths.productAnalyticsEvents), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(toProductAnalyticsBatch(events)),
    credentials: "omit",
    cache: "no-store",
    keepalive: true,
  })
  return response.ok
}

const queue = new ProductAnalyticsQueue({
  enabled: analyticsEnabled(),
  transport: sendBatch,
})

let currentArea: ProductAnalyticsArea = "other"
let currentAuthState: ProductAnalyticsAuthState = "anonymous"

export const setProductAnalyticsContext = (
  area: ProductAnalyticsArea,
  authState: ProductAnalyticsAuthState,
) => {
  currentArea = area
  currentAuthState = authState
}

export const trackProductEvent = (eventName: ProductAnalyticsEventName): void => {
  try {
    queue.enqueue({
      eventName,
      area: currentArea,
      authState: currentAuthState,
    })
  } catch {
    // Deliberately isolated: analytics must never block or break UX.
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    void queue.flush()
  })
}
